/**
 * Event Check-ins Tab
 * Lists every event this contact has CHECKED IN to (attended), newest first.
 * Read-only. Driven by event_attendees.checked_in_at, which all check-in paths
 * set (QR/code scan, staff walk-in, public self check-in).
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getContactEventCheckins,
  type EventCheckinRecord,
} from "../../services/contacts.service";
import { formatDate } from "../../utils/formatters";
import LoadingSpinner from "../common/LoadingSpinner";
import type { UUID } from "../../types/common.types";

interface EventCheckinsTabProps {
  contactId: UUID;
}

// Human label for how/where the check-in happened, when we have it.
function checkinMethodLabel(record: EventCheckinRecord): string | null {
  const loc = record.check_in_location?.trim();
  if (loc) {
    return loc;
  }
  const src = record.registration_source?.trim();
  if (src) {
    // e.g. "self_checkin" -> "Self check-in"
    const pretty = src.replace(/[_-]+/g, " ").trim();
    return pretty.charAt(0).toUpperCase() + pretty.slice(1);
  }
  return null;
}

function formatCheckinTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return formatDate(iso);
  }
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function EventCheckinsTab({ contactId }: EventCheckinsTabProps) {
  const backTo = encodeURIComponent(`/contacts/${contactId}`);

  const { data: checkins = [], isLoading } = useQuery({
    queryKey: ["contact-event-checkins", contactId],
    queryFn: () => getContactEventCheckins(contactId),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (checkins.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No event check-ins
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          This contact has not been checked in to any event yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-semibold text-green-600">
            {checkins.length}
          </div>
          <div className="text-xs text-gray-500">
            Event{checkins.length === 1 ? "" : "s"} attended
          </div>
        </div>
      </div>

      {/* Check-in list */}
      <div className="space-y-3">
        {checkins.map((c) => {
          const title = c.event?.title || "Untitled event";
          const method = checkinMethodLabel(c);
          const eventDate = c.event?.start_date || c.event?.start_time || null;
          return (
            <div
              key={c.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-green-50 flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/events/${c.event_id}?backTo=${backTo}`}
                      className="font-medium text-primary-600 hover:text-primary-700"
                    >
                      {title}
                    </Link>
                    <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                      {eventDate && <span>Event: {formatDate(eventDate)}</span>}
                      {c.event?.location_name && (
                        <span>· {c.event.location_name}</span>
                      )}
                    </div>
                    {method && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        Checked in via {method}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-green-700 bg-green-50">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Checked in
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatCheckinTime(c.checked_in_at)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
